/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Row,
  Typography,
  DatePicker,
  Tree,
  Spin,
  Button,
  Drawer,
  Divider,
  Badge,
  Checkbox,
  Col
} from 'antd';
import useAuthContext from '../../contexts/AuthContext';
import useFiltersContext from './context/DashboardContext';
import { useStateWithSessionStorage } from '../../utils';
import OwnerSelect from './OwnerSelect';

const { Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { TreeNode } = Tree;

const RSEFilters = () => {
  const { user, dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const {
    timeScale,
    dispatchTime,
    filters,
    dispatchFilters
  } = useFiltersContext();
  const { subsidiaries: sub, genre, owner } = filters;
  const [payload, setPayload] = useState({});
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [genres, setGenres] = useState([]);
  const [buttonValue, setButtonValue] = useStateWithSessionStorage(
    'timeScaleButtonKey'
  );
  const [pickerValue, setPickerValue] = useStateWithSessionStorage(
    'timeScalePickerValue'
  );
  const checkIfFilterActive = !!(genre.length || sub.length || owner);

  const fetchSubsidiaries = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/subsidiaries/tree-organization?company=${user.company}`
      });
      if (data) setSubsidiaries([data]);
      else setSubsidiaries([]);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDistinctsGenre = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/assets/distinctsGenre`
      });
      if (data) setGenres(data);
      else setGenres([]);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSubsidiaries();
    getDistinctsGenre();
  }, [fetchSubsidiaries]);

  const renderTreeNodes = (data) =>
    data.map((item) => {
      if (item.children) {
        return (
          <TreeNode
            title={<Typography href="">{item.title}</Typography>}
            key={item.key}
            dataRef={item}
          >
            {renderTreeNodes(item.children)}
          </TreeNode>
        );
      }
      return (
        <TreeNode
          title={<Typography href="">{item.title}</Typography>}
          key={item.key}
          dataRef={item}
        />
      );
    });

  const applyFilters = () => {
    dispatchFilters({ type: 'add', payload });
  };

  const resetFilters = () => {
    setPayload({});
    dispatchFilters({ type: 'reset' });
  };

  const handleRangeChange = (date) => {
    dispatchTime({ initiator: 'picker', payload: date });
    setPickerValue(date);
    setButtonValue(null);
  };

  useEffect(() => {
    if (!buttonValue && pickerValue)
      dispatchTime({
        initiator: 'picker',
        payload: [moment(pickerValue[0]), moment(pickerValue[1])]
      });
    setPayload({
      ...payload,
      ...(sub ? { subsidiaries: sub } : {}),
      ...(genre ? { genre } : {})
    });
  }, []);

  return (
    <>
      <Badge
        dot={checkIfFilterActive}
        style={{
          position: 'fixed',
          top: '35%',
          right: 40,
          width: 10,
          height: 10
        }}
      >
        <Button
          type="primary"
          onClick={() => setVisible(true)}
          size="large"
          icon="setting"
          style={{
            position: 'fixed',
            top: '35%',
            right: 0,
            fontSize: 26,
            borderRightColor: 'none',
            borderRadius: '8px 0 0 8px',
            boxShadow: '-3px 0 3px inset rgba(0, 0, 0, 0.2)',
            zIndex: 100
          }}
        />
      </Badge>
      <Drawer
        className="options-drawer"
        drawerStyle={{ overflowX: 'hidden' }}
        title="Options"
        destroyOnClose
        width="300"
        visible={visible}
        onClose={() => setVisible(false)}
      >
        <Paragraph strong>{t('dashboard.ITFilters.ownerTitle')}</Paragraph>
        <Row
          style={{
            margin: '0 -25px 8px -25px',
            padding: '8px 25px 8px 25px',
            backgroundColor: 'rgba(0,0,0,0.1)'
          }}
        >
          <OwnerSelect />
        </Row>
        <Divider />
        <Paragraph strong>{t('dashboard.ITFilters.pickerTitle')}</Paragraph>
        <Row style={{ marginBottom: 8 }}>
          <RangePicker
            allowClear={false}
            value={timeScale}
            onChange={handleRangeChange}
          />
        </Row>
        <Divider />
        <div style={{ width: '100%', marginBottom: 16, textAlign: 'center' }}>
          <Button onClick={applyFilters} type="primary">
            {t('dashboard.ITFilters.filterButton')}
          </Button>
          <Button onClick={resetFilters} type="link" icon="undo" />
        </div>
        <Paragraph style={{ marginTop: 16 }} strong>
          {t('dashboard.telecomFilters.deviceTitle')}
        </Paragraph>
        <Checkbox.Group
          style={{ width: '100%', color: 'white' }}
          onChange={(value) => setPayload({ ...payload, genre: value })}
          value={payload.genre}
        >
          <Row>
            {genres.map((d) => (
              <Col span={24} key={d}>
                <Checkbox value={d}>{d}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
        <Paragraph style={{ marginTop: 16 }} strong>
          {t('dashboard.ITFilters.subsidiariesTitle')}
        </Paragraph>
        <Spin spinning={isLoading} size="large">
          {subsidiaries.length ? (
            <Tree
              checkable
              selectable={false}
              checkedKeys={payload.subsidiaries}
              onCheck={(keys) => setPayload({ subsidiaries: keys })}
            >
              {renderTreeNodes(subsidiaries)}
            </Tree>
          ) : (
            t('dashboard.ITFilters.noSubsidiaries')
          )}
        </Spin>
      </Drawer>
    </>
  );
};

export default RSEFilters;
